import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { deleteInitPayout } from 'src/api/stoApi';
import { AssetDetailsLinkList } from 'src/components/assets/AssetDetailsLinkList';
import { RemainingBalanceFromLastPayment } from 'src/components/assets/payout/RemainingBalanceFromLastPayment';
import { PayoutTransactions } from 'src/components/assets/tables/PayoutTransactions';
import { SiteHeader } from 'src/components/ui/SiteHeader';
import { usePayouts } from 'src/hooks/usePayouts';
import { useSPV } from 'src/hooks/useSPV';
import { Payout } from 'src/models/';
import { TAssetStage } from '@brickwise/asset-validation/dist/schema/enums';
import { Routes } from 'src/router/Routes';
import { awaitUtil, calcRemainingRentFromLastPayment, payoutFilterPendingInitQueued } from 'src/util/utils';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { useAsset } from '../../context-providers/AssetContext';

export const AssetInvestorPayoutSite = () => {
  const { assetId } = useParams<{ assetId: string }>();
  const [refresher, setRefresher] = useState(true);
  const { asset } = useAsset();
  const payouts = usePayouts(assetId ?? '', refresher);
  const spv = useSPV(asset?.investmentDetails?.spv?.id ?? '');
  const remainingRent = calcRemainingRentFromLastPayment(payouts ?? [], true) as string;
  const payoutsInitOrPendingBool = payouts && payoutFilterPendingInitQueued(payouts).length > 0 ? true : false;
  const { t } = useTranslation();

  const removeInitPayout = async (id: Payout['id']) => {
    const [response] = await awaitUtil(deleteInitPayout(id));
    if (response) setRefresher(!refresher);
  };

  return (
    <>
      <SiteHeader
        breadcrumbs={[
          {
            link: Routes.ASSETS.ROOT,
            label: t('dashboard.breadcrumbs.assets'),
            icon: <ApartmentOutlinedIcon sx={{ mr: 0.5 }} fontSize="inherit" />,
          },
          {
            link: Routes.ASSETS.EDIT.replace(':assetId', asset?.id ?? ''),
            label: asset?.descriptions.title ?? '',
          },
          {
            link: Routes.ASSETS.INVESTMENT.replace(':assetId', asset?.id ?? ''),
            label: 'Payout Transactions',
          },
        ]}
        headline={(asset?.descriptions.title ?? '') + ' Payout Transactions'}
      >
        {asset && (
          <AssetDetailsLinkList
            assetId={asset.id ?? ''}
            useSerialNumber={asset.useSerialNumber}
            active={'payout'}
            isPrimary={asset?.investmentDetails.stage === TAssetStage.primary}
            isSecondary={asset?.investmentDetails.stage === TAssetStage.secondary}
            isPreorder={asset?.investmentDetails.stage === TAssetStage.preorder}
            securityType={asset?.investmentDetails?.securityType}
          />
        )}
      </SiteHeader>
      <RemainingBalanceFromLastPayment
        assetId={asset?.id}
        spvId={asset?.investmentDetails.spv?.id}
        remainingRent={remainingRent}
        payoutLocked={payoutsInitOrPendingBool}
      />
      <div style={{ marginTop: '20px' }}></div>
      {payouts && spv && asset && <PayoutTransactions payouts={payouts} removeInitPayout={removeInitPayout} spv={spv} asset={asset} />}
    </>
  );
};
